import { FC, useCallback, useContext, useState } from "react";
import { Modal, Tabs } from "antd";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { EProductStatus, IProduct, IProductCategory } from "../types";
import { Nullable } from "../../../app/types";
import { useAppNotifications } from "../../../components/LMNotifications";
import { AbilityContext } from "../../casl";
import { UpdateProductForm } from "../UpdateProductForm";
import { IMetadataEntry } from "../../metadata/types";
import { H3 } from "../../../components/Typography";
import styles from "./ProductPortfolioDetails.module.css";
import { getTranslation } from "../../i18n";
import { ProductIdentifiers } from "../ProductIdentifiers";
import { ProductModifiers } from "../ProductModifiers";
import { ProductPrices } from "../ProductPrices";
import { IconTrash } from "../../../assets";
import { ConfirmationModal } from "../../../components/ConfirmationModal";
import { deleteProduct } from "../api";
import { refreshList } from "../slice";
import { AppDispatch } from "../../../app/store";
import { LMIconButton } from "../../../components/LMIconButton";

interface Props {
  product: Nullable<IProduct>;
  categories: IProductCategory[];
  metadataEntries: IMetadataEntry[];
  onHide: () => void;
}

export const ProductPortfolioDetails: FC<Props> = ({
  product,
  onHide,
  categories,
  metadataEntries,
}) => {
  const { t } = useTranslation("productsPortfolio");
  const { contextHolder, success, error } = useAppNotifications();
  const ability = useContext(AbilityContext);
  const dispatch = useDispatch<AppDispatch>();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const hasRightsToUpdate = useCallback(() => {
    if (ability.can("manage", "products")) {
      return true;
    }
    if (product?.status === EProductStatus.Active) {
      return false;
    }
    return ability.can("managing", "products");
  }, [ability, product]);

  const handleUpdateSuccess = () => {
    success(t("updateProductSuccess"));
  };

  const handleUpdateError = () => {
    error(t("updateProductFail"));
  };

  const handleUpdatePriceSuccess = () => {
    success(t("priceChangeSuccess"));
  };

  const handleUpdatePriceError = () => {
    error(t("priceChangeError"));
  };

  const handleDeleteProduct = async () => {
    if (!product) return;
    try {
      await deleteProduct(product.id);
      await dispatch(refreshList());
      success(t("deleteProductSuccess"));
      setShowDeleteConfirm(false);
      onHide();
    } catch {
      error(t("deleteProductFail"));
      setShowDeleteConfirm(false);
    }
  };

  return (
    <>
      <Modal
        title={
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingRight: 24 }}>
            <H3>{product ? getTranslation(product?.name) : ""}</H3>
            {product && hasRightsToUpdate() && (
              <LMIconButton
                onClick={() => setShowDeleteConfirm(true)}
                style={{ color: "red" }}
              >
                <IconTrash />
              </LMIconButton>
            )}
          </div>
        }
        onCancel={onHide}
        className={styles.drawer}
        open={!!product}
        footer={null}
      >
        {product && (
          <Tabs
            centered
            className={styles.tabs}
            items={[
              {
                label: t("details"),
                key: "details",
                children: (
                  <UpdateProductForm
                    editable={hasRightsToUpdate()}
                    product={product}
                    categories={categories}
                    metadataEntries={metadataEntries}
                    onClose={onHide}
                    onSuccess={handleUpdateSuccess}
                    onError={handleUpdateError}
                    key={`${product.id}-${product.updatedAt.toString()}`}
                  />
                ),
              },
              {
                label: t("identifiers"),
                key: "identifiers",
                children: (
                  <ProductIdentifiers productId={product.id} onClose={onHide} />
                ),
              },
              {
                label: t("prices"),
                key: "prices",
                children: (
                  <ProductPrices
                    product={product}
                    onClose={onHide}
                    onSuccess={handleUpdatePriceSuccess}
                    onError={handleUpdatePriceError}
                    key={`${product.id}-${product.updatedAt.toString()}`}
                  />
                ),
              },
              // {
              //   label: t("modifiers"),
              //   key: "modifiers",
              //   children: (
              //     <ProductModifiers productId={product.id} onClose={onHide} />
              //   ),
              // },
            ]}
          ></Tabs>
        )}
      </Modal>
      <ConfirmationModal
        open={showDeleteConfirm}
        onConfirm={handleDeleteProduct}
        onCancel={() => setShowDeleteConfirm(false)}
        confirmButtonColor="danger-fluid"
        confirmText={t("deleteProduct")}
        content={
          <>
            <H3>{t("deleteProduct")}</H3>
            <p>{t("deleteProductConfirmation")}</p>
          </>
        }
      />
      {contextHolder}
    </>
  );
};
